import { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import {
  LayoutDashboard, BookOpen, Mic, Newspaper, Handshake,
  Award, Image, Briefcase, Mail, Settings, LogOut, Menu, X, ChevronLeft, Users, Heart
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const navItems = [
  { path: '/admin', label: 'לוח בקרה', icon: LayoutDashboard, exact: true },
  { path: '/admin/posts', label: 'סיפורים אישיים', icon: BookOpen },
  { path: '/admin/lectures', label: 'הרצאות', icon: Mic },
  { path: '/admin/media', label: 'תקשורת ומדיה', icon: Newspaper },
  { path: '/admin/partners', label: 'שותפים', icon: Handshake },
  { path: '/admin/certificates', label: 'תעודות והסמכות', icon: Award },
  { path: '/admin/gallery', label: 'גלריה', icon: Image },
  { path: '/admin/cv', label: 'קורות חיים', icon: Briefcase },
  { path: '/admin/good-deeds', label: 'הישגים בולטים', icon: Heart },
  { path: '/admin/leads', label: 'פניות', icon: Mail },
  { path: '/admin/users', label: 'משתמשים', icon: Users },
  { path: '/admin/settings', label: 'הגדרות', icon: Settings },
];

const AdminLayout = () => {
  const { user, signOut, roles } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isActive = (path: string, exact?: boolean) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);

  return (
    <div className="min-h-screen bg-background flex" dir="rtl">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 right-0 z-50 w-64 bg-card border-s border-border transform transition-transform duration-200
        lg:relative lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-border flex items-center justify-between">
            <Link to="/admin" className="text-lg font-bold text-gradient-gold">
              ניהול האתר
            </Link>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-muted-foreground">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Nav */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {navItems.map(({ path, label, icon: Icon, exact }) => (
              <Link
                key={path}
                to={path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive(path, exact)
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="truncate">{label}</span>
              </Link>
            ))}
          </nav>

          {/* User footer */}
          <div className="p-4 border-t border-border">
            <div className="text-xs text-muted-foreground mb-2 truncate">{user?.email}</div>
            <div className="text-xs text-primary/70 mb-3">{roles.join(', ')}</div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1" asChild>
                <Link to="/he">
                  <ChevronLeft className="w-3 h-3 me-1" />
                  לאתר
                </Link>
              </Button>
              <Button variant="ghost" size="sm" onClick={signOut}>
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main content */}
      <main className="flex-1 min-h-screen">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 h-14 flex items-center">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-foreground me-2">
            <Menu className="w-5 h-5" />
          </button>
          <h2 className="text-sm font-medium text-foreground truncate">
            {navItems.find(n => isActive(n.path, n.exact))?.label || 'ניהול'}
          </h2>
        </header>

        <div className="p-4 lg:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
